import * as React from "react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Navbar from '../../components/navbar.jsx';
import SiteFooter from '../../components/siteFooter.jsx';
import AccountForms, { CodeEntry } from '../../components/accountForms.jsx';
import { useAccount } from '../../components/accountContext.jsx';
import { formatRupees } from '../../components/billzappPlans.jsx';
import { POS_PORTAL_URL } from '../../data/billzappPlans';

const defaultTheme = createTheme();

const formatDate = (date) => (date ? new Date(date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "");

// How the BillZapp plan stands, from the profile's accountStatus and dates.
function planStatus(profile) {
  switch (profile.accountStatus) {
    case "active":
      return { label: "Active", color: "success", text: `Paid until ${formatDate(profile.subscriptionExpiresAt)}.` };
    case "trial":
      return { label: "Free trial", color: "info", text: `Your free trial ends on ${formatDate(profile.trialEndDate)}.` };
    case "expired":
      return { label: "Expired", color: "warning", text: `Renew by ${formatDate(profile.graceEndsAt)} to keep using BillZapp.` };
    default:
      return { label: "Expired", color: "error", text: "Renew to start using BillZapp again." };
  }
}

const DELIVERY = {
  pending: { label: "Preparing to ship", color: "default" },
  shipped: { label: "Shipped", color: "info" },
  delivered: { label: "Delivered", color: "success" },
  cancelled: { label: "Cancelled", color: "error" },
};

function DetailRow({ label, children }) {
  return (
    <Box sx={{ display: 'flex', gap: 2, py: 1 }}>
      <Typography color="text.secondary" sx={{ width: 140, flexShrink: 0 }}>{label}</Typography>
      <Typography sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>{children || "-"}</Typography>
    </Box>
  );
}

// The account email, and changing it: a code goes to the new address first. Also verifies the
// current address of an account made in the BillZapp app, which was never checked.
function EmailSetting() {
  const { profile, setProfile, request } = useAccount();
  const [step, setStep] = useState("view"); // "view" | "enter" | "code"
  const [email, setEmail] = useState("");
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [saved, setSaved] = useState("");

  const start = (initialEmail) => {
    setEmail(initialEmail);
    setMessage("");
    setSaved("");
    setStep("enter");
  };

  const sendCode = async (event) => {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      setPending(await request("/account/email", { method: "POST", body: { email } }));
      setStep("code");
    } catch (err) {
      setMessage(err.message);
    }
    setBusy(false);
  };

  if (step === "code") {
    return (
      <Box sx={{ py: 1 }}>
        <CodeEntry
          // Not {...pending}: its `ref` field would be taken as a React ref.
          email={pending.email}
          codeMinutes={pending.codeMinutes}
          resendInSeconds={pending.resendInSeconds}
          verifyLabel="Confirm email"
          backLabel="Use another email"
          onBack={() => setStep("enter")}
          onResend={() => request("/account/code/resend", { method: "POST", body: { ref: pending.ref } })}
          onVerify={async (code) => {
            const data = await request("/account/email/verify", { method: "POST", body: { ref: pending.ref, code } });
            setProfile(data.profile);
            setSaved(`Your email is now ${data.profile.email}.`);
            setStep("view");
          }}
        />
      </Box>
    );
  }

  if (step === "enter") {
    return (
      <Box component="form" noValidate onSubmit={sendCode} sx={{ py: 1 }}>
        {message && <Alert severity="error" sx={{ mb: 2 }}>{message}</Alert>}
        <TextField
          label="Email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          autoComplete="email"
          disabled={busy}
          autoFocus
          fullWidth
          helperText="We email a code to this address to confirm it."
        />
        <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
          <Button type="submit" variant="contained" disabled={busy}>
            {busy ? <CircularProgress size={22} color="inherit" /> : "Send code"}
          </Button>
          <Button onClick={() => setStep("view")} disabled={busy}>Cancel</Button>
        </Stack>
      </Box>
    );
  }

  return (
    <>
      {saved && <Alert severity="success" sx={{ my: 1 }}>{saved}</Alert>}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1, flexWrap: 'wrap' }}>
        <Typography color="text.secondary" sx={{ width: 140, flexShrink: 0 }}>Email</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0, flexWrap: 'wrap' }}>
          <Typography sx={{ overflowWrap: 'anywhere' }}>{profile.email || "-"}</Typography>
          {profile.email && (
            <Chip
              label={profile.emailVerified ? "Verified" : "Not verified"}
              color={profile.emailVerified ? "success" : "warning"}
              size="small"
              variant="outlined"
            />
          )}
        </Box>
        <Box>
          {!profile.emailVerified && profile.email && (
            <Button size="small" onClick={() => start(profile.email)}>Verify</Button>
          )}
          <Button size="small" onClick={() => start("")}>Change</Button>
        </Box>
      </Box>
    </>
  );
}

function BusinessDetails() {
  const { profile, setProfile, request } = useAccount();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [saved, setSaved] = useState(false);

  const startEditing = () => {
    setForm({ businessName: profile.businessName, address: profile.address, phone: profile.phone });
    setMessage("");
    setSaved(false);
    setEditing(true);
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      const data = await request("/account/profile", { method: "PUT", body: form });
      setProfile(data.profile);
      setEditing(false);
      setSaved(true);
    } catch (err) {
      setMessage(err.message);
    }
    setBusy(false);
  };

  return (
    <Card elevation={2} sx={{ p: 3, height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" fontWeight="bold">Business details</Typography>
        {!editing && <Button onClick={startEditing}>Edit</Button>}
      </Box>
      {saved && <Alert severity="success" sx={{ mb: 1 }}>Saved. BillZapp uses these details on your bills too.</Alert>}

      {editing ? (
        <Box component="form" noValidate onSubmit={handleSave}>
          {message && <Alert severity="error" sx={{ mb: 2 }}>{message}</Alert>}
          <Stack spacing={2} sx={{ mt: 1 }}>
            {[
              ["businessName", "Business name", "organization"],
              ["address", "Business address", "street-address"],
              ["phone", "Mobile number", "tel"],
            ].map(([key, label, autoComplete]) => (
              <TextField
                key={key}
                label={label}
                value={form[key]}
                onChange={(event) => setForm({ ...form, [key]: event.target.value })}
                autoComplete={autoComplete}
                disabled={busy}
                multiline={key === "address"}
                fullWidth
              />
            ))}
          </Stack>
          <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
            <Button type="submit" variant="contained" disabled={busy}>
              {busy ? <CircularProgress size={22} color="inherit" /> : "Save"}
            </Button>
            <Button onClick={() => setEditing(false)} disabled={busy}>Cancel</Button>
          </Stack>
        </Box>
      ) : (
        <>
          <DetailRow label="Business name">{profile.businessName}</DetailRow>
          <DetailRow label="Address">{profile.address}</DetailRow>
          <DetailRow label="Mobile number">{profile.phone}</DetailRow>
          <DetailRow label="Username">{profile.username}</DetailRow>
        </>
      )}
      <Divider sx={{ my: 1 }} />
      <EmailSetting />
    </Card>
  );
}

function PlanCard() {
  const { profile } = useAccount();
  const status = planStatus(profile);
  const lapsed = profile.accountStatus === "expired" || profile.accountStatus === "suspended";

  return (
    <Card elevation={2} sx={{ p: 3, height: '100%' }}>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>BillZapp plan</Typography>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h5" fontWeight="bold" color="primary">{profile.planName || "BillZapp"}</Typography>
        <Chip label={status.label} color={status.color} size="small" />
      </Stack>
      <Typography color="text.secondary" sx={{ mb: 3 }}>{status.text}</Typography>
      <Stack spacing={1.5}>
        <Button href={lapsed ? `${POS_PORTAL_URL}/login` : POS_PORTAL_URL} variant="contained">
          {lapsed ? "Renew in BillZapp" : "Open BillZapp"}
        </Button>
        <Button href="/products" variant="outlined">Buy a Pocket Billing Machine</Button>
      </Stack>
    </Card>
  );
}

function OrderCard({ order }) {
  const delivery = DELIVERY[order.delivery] || DELIVERY.pending;
  const { deliverTo } = order;

  return (
    <Card elevation={2} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 1, mb: 1 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight="bold">{order.product}</Typography>
          <Typography variant="body2" color="text.secondary">
            Order {order.orderNumber} · Paid {formatDate(order.paidAt)}
          </Typography>
        </Box>
        <Typography variant="h6" fontWeight="bold">{formatRupees(order.amount)}</Typography>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, my: 1.5 }}>
        <Chip label={`Printer: ${delivery.label}`} color={delivery.color} size="small" variant="outlined" />
        {order.softwareYear === "added" && (
          <Chip label={`Software: added, runs until ${formatDate(order.softwarePeriodEnd)}`} color="success" size="small" variant="outlined" />
        )}
        {order.softwareYear === "elsewhere" && (
          <Chip label="Software: added to another BillZapp account" size="small" variant="outlined" />
        )}
      </Box>

      <Divider sx={{ my: 1.5 }} />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2" color="text.secondary">
            {order.printerName} receipt printer: {formatRupees(order.printerAmount)}
            <br />
            {order.planName} (1 year): {formatRupees(order.planAmount)}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2" color="text.secondary">
            Deliver to {deliverTo.name}, {[deliverTo.businessName, deliverTo.address, deliverTo.city].filter(Boolean).join(", ")},{" "}
            {deliverTo.state} {deliverTo.pincode} · {deliverTo.phone}
          </Typography>
        </Grid>
      </Grid>

      {order.softwareYear === "pending" && order.claimUrl && (
        <Alert severity="warning" sx={{ mt: 2 }} action={<Button href={order.claimUrl} color="inherit" size="small">Add it</Button>}>
          The year of software isn't on your account yet.
        </Alert>
      )}
    </Card>
  );
}

function Purchases() {
  const { request } = useAccount();
  const [purchases, setPurchases] = useState(null);
  const [message, setMessage] = useState("");

  useEffect(() => {
    let cancelled = false;
    request("/account/purchases")
      .then((data) => !cancelled && setPurchases(data))
      .catch((err) => !cancelled && setMessage(err.message));
    return () => {
      cancelled = true;
    };
  }, [request]);

  if (message) return <Alert severity="error">{message}</Alert>;
  if (!purchases) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (purchases.orders.length === 0 && purchases.subscriptions.length === 0) {
    return (
      <Card elevation={2} sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>No purchases yet</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Orders for Pocket Billing Machines and BillZapp plan payments show up here.
        </Typography>
        <Button href="/products" variant="contained">See products</Button>
      </Card>
    );
  }

  return (
    <Stack spacing={4}>
      <Box>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Pocket Billing Machine orders</Typography>
        {purchases.orders.length === 0 ? (
          <Typography color="text.secondary">
            No orders yet. <Button href="/products" size="small">See products</Button>
          </Typography>
        ) : (
          <Stack spacing={2}>
            {purchases.orders.map((order) => <OrderCard key={order.orderNumber} order={order} />)}
          </Stack>
        )}
      </Box>

      {purchases.subscriptions.length > 0 && (
        <Box>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>BillZapp plan payments</Typography>
          <TableContainer component={Card} elevation={2}>
            <Table size="small" sx={{ minWidth: 560 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Plan</TableCell>
                  <TableCell>Paid on</TableCell>
                  <TableCell>Covers until</TableCell>
                  <TableCell>Invoice</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {purchases.subscriptions.map((payment, index) => (
                  <TableRow key={`${payment.paidAt}-${index}`}>
                    <TableCell>{payment.planName} ({payment.billingCycle === "annual" ? "yearly" : "monthly"})</TableCell>
                    <TableCell>{formatDate(payment.paidAt)}</TableCell>
                    <TableCell>{formatDate(payment.periodEnd)}</TableCell>
                    <TableCell>{payment.invoiceNumber || "-"}</TableCell>
                    <TableCell align="right">{formatRupees(payment.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Stack>
  );
}

// "My Account": log in or sign up, then the profile and purchases of the BillZapp account.
export default function AccountPage() {
  const { status, profile, signOut, refreshProfile } = useAccount();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "purchases" ? "purchases" : "profile";

  // The plan may have changed since the login was saved (e.g. renewed in the app).
  useEffect(() => {
    if (status === "signedIn") refreshProfile().catch(() => {});
    // Only when the page opens or the visitor logs in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  return (
    <ThemeProvider theme={defaultTheme}>
      <CssBaseline />
      <Navbar />

      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1976d2 0%, #00bcd4 100%)',
          color: 'white',
          pt: { xs: 12, md: 14 }, // clears the fixed navbar
          pb: { xs: 5, md: 6 },
          textAlign: 'center'
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h1" sx={{ fontSize: { xs: '2.2rem', md: '3.2rem' }, fontWeight: 800, mb: 1 }}>
            My Account
          </Typography>
          <Typography variant="h6" sx={{ fontSize: { xs: '1rem', md: '1.25rem' }, opacity: 0.9 }}>
            {status === "signedIn" ? profile.businessName : "Your BillZapp account, purchases and orders"}
          </Typography>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 5, md: 7 }, bgcolor: 'grey.50', minHeight: '50vh' }}>
        {status === "checking" ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : status === "signedOut" ? (
          <Container maxWidth="sm">
            <Card elevation={3} sx={{ p: { xs: 2.5, sm: 4 } }}>
              <Typography variant="h5" fontWeight="bold" gutterBottom>Log in or create an account</Typography>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                Your website account is your BillZapp account: log in with your BillZapp owner
                username, or create one to buy a Pocket Billing Machine.
              </Typography>
              <AccountForms />
            </Card>
          </Container>
        ) : (
          <Container maxWidth="lg">
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 3 }}>
              <Tabs value={tab} onChange={(event, value) => setSearchParams(value === "purchases" ? { tab: value } : {})}>
                <Tab value="profile" label="Profile" />
                <Tab value="purchases" label="Purchases" />
              </Tabs>
              <Button onClick={signOut} color="inherit">Log out</Button>
            </Box>

            {tab === "profile" ? (
              <Grid container spacing={3}>
                <Grid item xs={12} md={7}><BusinessDetails /></Grid>
                <Grid item xs={12} md={5}><PlanCard /></Grid>
              </Grid>
            ) : (
              <Purchases />
            )}
          </Container>
        )}
      </Box>

      <SiteFooter />
    </ThemeProvider>
  );
}
